<template>
  <panel-layout
    id="serviceCategory-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #actions-right>
      <div class="flex items-center">
        <o-tooltip v-if="serviceCategory" :text="$t('general.delete')">
          <o-button icon="delete" flat @click="handleDelete" />
        </o-tooltip>
      </div>
    </template>

    <template #heading>
      <div v-if="serviceCategory" class="flex items-center space-x-2">
        <div>
          <h2
            class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100"
          >
            {{
              serviceCategory.isNewRecord
                ? $t('service.newCategory')
                : serviceCategory.name
            }}
          </h2>
          <div v-if="!serviceCategory.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200" v-html="$t('pages.serviceCategory.edit.id', { id: serviceCategory.idServiceCategory })" />
          </div>
        </div>
      </div>
    </template>

    <o-form ref="form" class="h-full w-full">
      <o-scroll class="h-full w-full">
        <div class="w-full px-6 py-4">
        <template v-if="serviceCategory">
          <div class="flex space-x-4 mb-6">
            <o-field-group
              v-slot="{ status }"
              rules="required"
              class="w-full"
              :label="$t('general.title')"
              :info="$t('pages.serviceCategory.edit.name')"
            >
              <text-field
                v-model="serviceCategory.name"
                :label="$t('general.title')"
                :status="status"
              />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group
              class="w-full"
              :label="$t('general.information')"
              :info="$t('service.categoryDetails')"
            >
              <text-area-field
                v-model="serviceCategory.information"
                :label="$t('general.information')"
              />
            </o-field-group>
          </div>

          <div v-if="!serviceCategory.isNewRecord" class="flex space-x-4 mb-6">
            <o-field-group :label="$t('general.image')" :info="$t('product.visibleInLists')">
              <avatar-field
                :model="serviceCategory"
                @click="handleUpdateImage"
              />
            </o-field-group>
          </div>
        </template>

        <div v-if="services" class="flex space-x-4 mb-6">
          <div class="w-full">
            <div class="flex items-center justify-between">
              <o-field-group
                class="w-full"
                :label="$t('general.services')"
                :info="$t('pages.serviceCategory.edit.services')"
              />
              <o-button variant="primary" icon="add" class="whitespace-nowrap" @click="handleAddService">
                {{ $t('service.addService') }}
              </o-button>
            </div>

            <div
              v-if="!services.length"
              class="text-sm text-center p-4 bg-gray-50 rounded-lg dark:bg-gray-900"
            >
              <p class="text-gray-800 font-semibold dark:text-gray-100">
                {{ $t('pages.serviceCategory.edit.noServices') }}
              </p>
              <p class="text-gray-600 dark:text-gray-300">
                {{ $t('pages.serviceCategory.edit.addServices') }}
              </p>
            </div>

            <draggable v-model="services" handle=".reorder-handle" item-key="idService" class="pt-3">
              <template #item="{ element: service }">
                <div
                  v-if="service"
                  class="bg-gray-50 rounded-lg mb-1 p-2 dark:bg-gray-900"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-1">
                      <o-button
                        icon="reorderHandle"
                        size="sm"
                        flat
                        class="reorder-handle"
                      />

                      <div class="text-sm leading-none pl-4 flex items-center">
                        <p
                          class="font-semibold text-gray-800 pr-3 border-r mr-3 dark:text-gray-100"
                        >
                          {{ service.title }}
                          <span class="text-xs font-normal text-gray-600 dark:text-gray-300">(#{{ service.idService }})</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </draggable>
          </div>
        </div>
        </div>
      </o-scroll>
    </o-form>

    <template #footer>
      <div v-if="serviceCategory || services">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button
              variant="primary"
              outline
              icon="save"
              class="mr-2"
              @click="save()"
            >
              {{ $t('general.save') }}
            </o-button>
            <o-button
              variant="primary"
              icon="save"
              class="mr-2"
              @click="save(true)"
            >
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <template v-if="serviceCategory">
      <update-image-dialog
        ref="updateImageDialog"
        :serviceCategory="serviceCategory"
        @update="handleUpdateImageSuccess"
      />

      <delete-dialog ref="deleteDialog" @success="cancel(true)" />

      <add-service-dialog
        ref="addServiceDialog"
        @success="handleAddServiceSuccess"
      />
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        {
          text: $t('general.closeWithoutSaving'),
          type: 'confirm',
          variant: undefined,
          flat: true,
          outline: true,
          callback: () => cancel(true)
        },
        {
          text: $t('general.saveAndClose'),
          type: 'confirm',
          response: 'save',
          callback: () => save(true)
        }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
import draggable from 'vuedraggable'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
import DeleteDialog from '@/components/serviceCategory/actions/Delete.vue'
import UpdateImageDialog from '@/components/serviceCategory/actions/Image.vue'
import AddServiceDialog from '@/components/serviceCategory/actions/AddService.vue'

import { OAvatar, ODropdownItem } from '@/components/elements'

import { TextField, TextAreaField, AvatarField } from '@/components/fields'

import type { ServiceCategory } from '~/_api/models/serviceCategory'
import type { Service } from '~/_api/models/service'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const { $notification, $bus, $api, $i18n } = useNuxtApp()
const router = useRouter()

const serviceCategory = ref<ServiceCategory | null>(null)
const services = ref<Service[] | null>(null)
const errors = ref(false)
const loading = ref(true)

const confirmDialog = ref<any>()
const deleteDialog = ref<any>()
const updateImageDialog = ref<any>()
const addServiceDialog = ref<any>()
const form = ref<any>()

const { unsavedChanges, snapshot } = useUnsavedChanges(serviceCategory)

const fetchServiceCategory = async (id?: string) => {
  loading.value = true

  try {
    if (!id) {
      const response = await $api.serviceCategory.defaults()
      serviceCategory.value = response.data
    } else if (id !== '0') {
      const response = await $api.serviceCategory.get(id)
      serviceCategory.value = response.data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }

  loading.value = false
}

const fetchServices = async (id?: string) => {
  try {
    if (id) {
      const filters = {
        category: [id !== '0' ? id : null],
      }
      const _services = await $api.service.filterAll({
        filters,
        limit: 100,
      })

      services.value = _services?.sort((a: any, b: any) => {
        return (a?.listOrder ?? 0) - (b?.listOrder ?? 0)
      })
    }
  } catch (e) {
    console.log(e)
  }

  loading.value = false
}

const handleDelete = () => {
  deleteDialog.value.open(serviceCategory.value)
}

const handleUpdateImage = () => {
  updateImageDialog.value.open(serviceCategory.value)
}

const handleAddService = () => {
  addServiceDialog.value.open(serviceCategory.value)
}

const handleAddServiceSuccess = () => {
  return fetchServices(serviceCategory.value?.objectID())
}

const save = async (close = false) => {
  if (!await form.value?.validate()) return false

  loading.value = true
  errors.value = false

  try {
    const isNewRecord = (serviceCategory.value as any)?.isNewRecord
    let _serviceCategory: any = null

    if (serviceCategory.value) {
      const response = await $api.serviceCategory.save(serviceCategory.value)
      _serviceCategory = response.data
    }

    if (_serviceCategory) {
      snapshot()

      const reorder = services.value?.length
        ? await $api.service.reorder(services.value)
        : true

      if (reorder) {
        $notification.toast({
          title: $i18n.t('service.savedSuccess'),
          text: $i18n.t('pages.serviceCategory.edit.successInfo'),
          variant: 'success',
        })

        if (_serviceCategory) {
          $bus.emit.updated('ServiceCategory', _serviceCategory)
        }

        if (close) {
          panel.close()
        } else if (isNewRecord) {
          // Swap in-place to the freshly-created service category's edit view.
          panel.replace('serviceCategory/edit', { id: String(_serviceCategory.idServiceCategory) })
        }
      }
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const handleUpdateImageSuccess = (_serviceCategory: any) => {
  (serviceCategory.value as any).images = _serviceCategory.images;
  (serviceCategory.value as any).hasImage = !!_serviceCategory.images
  $bus.emit.updated('ServiceCategory')
}

watch(() => props.id, (id) => {
  fetchServiceCategory(id)
  fetchServices(id)
}, { immediate: true })
</script>
